'use client';

import Icon from '@/components/ui/AppIcon';
import { QuoteFormData } from './SmartQuoteForm';

interface QuoteSummaryProps {
  formData: QuoteFormData;
}

export default function QuoteSummary({ formData }: QuoteSummaryProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-headline font-bold text-foreground">
          Review Your Quote Request
        </h2>
        <p className="text-muted-foreground font-body">
          Please review all details before submitting your quote request.
        </p>
      </div>

      {/* Project Information */}
      <div className="bg-muted rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-2 pb-3 border-b border-border">
          <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-primary" />
          <h3 className="text-lg font-cta font-bold text-foreground">Project Information</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Project Name</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.projectName}</div>
          </div>
          {formData.companyName && (
            <div>
              <div className="text-xs text-muted-foreground font-body mb-1">Company Name</div>
              <div className="text-sm font-cta font-semibold text-foreground">{formData.companyName}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Contact Name</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.contactName}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Email</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.email}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Phone</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.phone}</div>
          </div>
        </div>
      </div>

      {/* Files */}
      <div className="bg-muted rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-2 pb-3 border-b border-border">
          <Icon name="DocumentTextIcon" size={24} variant="solid" className="text-primary" />
          <h3 className="text-lg font-cta font-bold text-foreground">Uploaded Files</h3>
        </div>
        <div className="space-y-2">
          {formData.files.map((file, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-background rounded-lg">
              <Icon name="DocumentIcon" size={20} variant="outline" className="text-primary" />
              <div className="text-sm font-body text-foreground">{file.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Material & Specifications */}
      <div className="bg-muted rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-2 pb-3 border-b border-border">
          <Icon name="CubeIcon" size={24} variant="solid" className="text-primary" />
          <h3 className="text-lg font-cta font-bold text-foreground">Material & Specifications</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Material</div>
            <div className="text-sm font-cta font-semibold text-foreground capitalize">{formData.material}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Grade</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.materialGrade}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Thickness</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.thickness}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Quantity</div>
            <div className="text-sm font-cta font-semibold text-foreground">{formData.quantity} units</div>
          </div>
          {formData.finishType && (
            <div>
              <div className="text-xs text-muted-foreground font-body mb-1">Finish Type</div>
              <div className="text-sm font-cta font-semibold text-foreground capitalize">{formData.finishType.replace('-', ' ')}</div>
            </div>
          )}
          {formData.deliveryTimeline && (
            <div>
              <div className="text-xs text-muted-foreground font-body mb-1">Delivery Timeline</div>
              <div className="text-sm font-cta font-semibold text-foreground capitalize">{formData.deliveryTimeline}</div>
            </div>
          )}
        </div>
        {formData.toleranceRequirements && (
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Tolerance Requirements</div>
            <div className="text-sm font-body text-foreground">{formData.toleranceRequirements}</div>
          </div>
        )}
        {formData.specialRequirements && (
          <div>
            <div className="text-xs text-muted-foreground font-body mb-1">Special Requirements</div>
            <div className="text-sm font-body text-foreground">{formData.specialRequirements}</div>
          </div>
        )}
      </div>

      {/* Next Steps */}
      <div className="bg-success/10 rounded-xl p-6 border border-success/20">
        <div className="flex items-start space-x-3">
          <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success flex-shrink-0" />
          <div className="space-y-2">
            <div className="text-sm font-cta font-semibold text-foreground">
              What happens after submission?
            </div>
            <ul className="text-sm text-muted-foreground font-body space-y-1">
              <li>• Our technical team will review your specifications within 2 hours</li>
              <li>• You'll receive a detailed quote via email within 24-48 hours</li>
              <li>• The quote will include pricing, lead times, and technical recommendations</li>
              <li>• A project manager will be assigned to answer any questions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}